import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DataLabel, fmtDate, daysUntil, urgencyClass } from "@/lib/utils-ui";
import ExportMenu from "@/components/ExportMenu";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = ["#FF5000","#FFB020","#1F6FEB","#888888","#0a7a3f","#222222","#FF7A00"];

export default function ReportsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("_all");
  const [tickets, setTickets] = useState([]);
  const [subs, setSubs] = useState([]);
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
    api.get("/tickets").then(r => setTickets(r.data)).catch(() => {});
    api.get("/subscriptions").then(r => setSubs(r.data)).catch(() => {});
    api.get("/assets").then(r => setAssets(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="p-10 data-label">Loading reports…</div>;

  const filterByCompany = (rows) => companyFilter === "_all" ? rows : rows.filter(r => r.company_id === companyFilter);
  const fTickets = filterByCompany(tickets);
  const fSubs = filterByCompany(subs);
  const fAssets = filterByCompany(assets);

  const ticketStatusData = ["open","assigned","in_progress","resolved","closed"].map(s => ({ name: s.replace("_"," "), count: fTickets.filter(t => t.status === s).length }));
  const ticketPriorityData = ["low","medium","high","critical"].map(p => ({ name: p, value: fTickets.filter(t => t.priority === p).length }));
  const assetStatusData = ["active","in_stock","repair","scrap","lost"].map(s => ({ name: s.replace("_"," "), count: fAssets.filter(a => a.status === s).length }));

  // Renewals by month (next 6)
  const renewalsByMonth = {};
  fSubs.forEach(s => {
    const d = new Date(s.expiry_date);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    renewalsByMonth[key] = (renewalsByMonth[key] || 0) + 1;
  });
  const renewalsTimeline = Object.entries(renewalsByMonth).sort().slice(0, 12).map(([k, v]) => ({ month: k, count: v }));

  const exportParams = companyFilter !== "_all" ? { company_id: companyFilter } : {};

  const reportRow = (label, count, module, accent) => (
    <div className="grid grid-cols-12 items-center border-b border-border" data-testid={`report-${module}`}>
      <div className="col-span-6 p-4">
        <div className="font-head font-bold uppercase text-sm tracking-wider">{label}</div>
      </div>
      <div className="col-span-3 p-4 text-right">
        <div className={`font-head font-black text-2xl tracking-tighter ${accent ? "text-[#FF5000]" : ""}`}>{count}</div>
      </div>
      <div className="col-span-3 p-4 flex justify-end">
        <ExportMenu module={module} params={exportParams} label="Export" />
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <DataLabel>Reports & Analytics</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Cross-module analytics. Export any dataset as Excel or CSV.</p>
        </div>
        {user?.role === "super_admin" && (
          <div className="flex items-center gap-2">
            <span className="data-label">Company filter</span>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="company-filter" className="w-56"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All companies</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 border border-border bg-white">
        {[
          ["Total Assets", fAssets.length, false],
          ["Assigned", fAssets.filter(a => a.assigned_user_id).length, false],
          ["In Store", fAssets.filter(a => a.status === "in_stock").length, false],
          ["Open Tickets", fTickets.filter(t => !["resolved","closed"].includes(t.status)).length, true],
          ["Renewals ≤30d", fSubs.filter(s => daysUntil(s.expiry_date) <= 30).length, true],
          ["Renewals ≤7d", fSubs.filter(s => daysUntil(s.expiry_date) <= 7).length, true],
        ].map(([l, v, accent], i, arr) => (
          <div key={l} className={`p-4 border-r border-border ${i === arr.length-1 ? "border-r-0" : ""} ${accent && v > 0 ? "bg-[#FFF4EB]" : ""}`}>
            <DataLabel>{l}</DataLabel>
            <div className="font-head font-black text-3xl tracking-tighter mt-1">{v}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 border border-border bg-white">
        <div className="p-6 lg:col-span-2 border-r border-border">
          <DataLabel>Ticket Status</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">Pipeline distribution</h3>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ticketStatusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <Tooltip contentStyle={{ borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}/>
                <Bar dataKey="count" fill="#FF5000"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-6">
          <DataLabel>Priority Mix</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">Tickets by priority</h3>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={ticketPriorityData} dataKey="value" nameKey="name" outerRadius={80}>
                  {ticketPriorityData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 border border-border bg-white">
        <div className="p-6 border-r border-border">
          <DataLabel>Asset Status</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">Distribution</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={assetStatusData} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} width={80}/>
                <Tooltip contentStyle={{ borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}/>
                <Bar dataKey="count" fill="#1F6FEB"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-6">
          <DataLabel>Renewals timeline</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">Subscriptions by month</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={renewalsTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <Tooltip contentStyle={{ borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}/>
                <Line type="monotone" dataKey="count" stroke="#FF5000" strokeWidth={2}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick exports */}
      <div className="border border-border bg-white">
        <div className="p-4 border-b border-border bg-muted">
          <DataLabel>One-click exports</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">Per-module reports</h3>
        </div>
        {reportRow("Inventory · Assets", fAssets.length, "assets", false)}
        {reportRow("Subscriptions", fSubs.length, "subscriptions", true)}
        {reportRow("Vendors / AMC", undefined, "vendors", false)}
        {reportRow("Audit Reports", stats.audits_total, "audits", false)}
        {reportRow("Invoices", stats.invoices_total, "invoices", false)}
        {reportRow("Tickets", fTickets.length, "tickets", true)}
        {(["super_admin","company_admin"].includes(user?.role)) && reportRow("Users", undefined, "users", false)}
        {(user?.role === "super_admin") && reportRow("Companies", companies.length, "companies", false)}
      </div>
    </div>
  );
}
